import { LocalDate } from "@js-joda/core";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../core/auth";
import type { BodyOf, ResponseOf } from "../../../../core/api";
import useApi from "../../../../shared/hooks/useApi";
import { queryKeys } from "../../../../shared/query-keys";
import type { CertificationPeriodId } from "../../../../shared/schema/schema";
import useEntityProfileToast from "../../../entityProfileModal/hooks/useEntityProfileToast";
import type { PatientProfilePatient } from "../../patientProfileModal.types";

export type PatientPlanOfCareResponse = ResponseOf<"get", "./patients/:patientId/plans_of_care">;
export type PatientDutySheetsResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/patients/:patientId/duty_sheets"
>;
export type PatientDocumentsResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/:agencyMemberId/patient_documents_history/:patientId"
>;
export type PatientBloodPressureResponse = ResponseOf<
  "get",
  "./patient/:patientId/patient_blood_pressure/history"
>;
export type PatientBloodPressureDeviceAssignmentResponse = ResponseOf<
  "put",
  "./patient/:patientId/patient_blood_pressure_device/assign/:imei"
>;
export type PatientMedicationsResponse = ResponseOf<"get", "./patients/:patientId/medications">;
export type PatientMedicalDiagnosisCodesResponse = ResponseOf<
  "get",
  "./patients/:patientId/patient_medical_diagnosis_codes"
>;
export type PatientTaskHistoryResponse = ResponseOf<
  "get",
  "./patients/:patientId/task_history_summary"
>;
export type PatientPrimaryPhysicianResponse = ResponseOf<
  "get",
  "./patients/:patientId/primary_physician"
>;
export type PatientAvailablePhysiciansResponse = ResponseOf<
  "get",
  "./patients/:patientId/available_physicians"
>;
export type PatientPhysicianAssociation = {
  address?: string | null;
  displayName?: string | null;
  fax?: string | null;
  id: number;
  isPrimary?: boolean | null;
  npi?: string | null;
  phone?: string | null;
  physicianId?: number | null;
};
export type PatientPhysiciansResponse = {
  physicians: PatientPhysicianAssociation[];
};
export type PatientMedicationProfileRow = {
  dose?: string | null;
  frequency?: string | null;
  from?: string | null;
  id: number;
  medicationId?: number | null;
  medicationName?: string | null;
  name?: string | null;
  notes?: string | null;
  route?: string | null;
  status?: string | null;
  until?: string | null;
};
export type PatientMedicationProfileResponse = {
  medications: PatientMedicationProfileRow[];
};
export type PatientCertificationPeriod = {
  endDate: string;
  hasTaskInstance?: boolean;
  id: CertificationPeriodId;
  isCompleted?: boolean;
  startDate: string;
  type: "REASSESSMENT" | "START_OF_CARE" | string;
};
export type PatientCertificationPeriodsResponse = {
  certificationPeriods: PatientCertificationPeriod[];
};
export type PatientVital = {
  answer: unknown;
  answerType?: string | null;
  questionText: string;
  reportedAgencyMember?: unknown;
  reportedCaregiverId?: number | null;
  reporterName?: string | null;
  vitalComments?: string | null;
  vitalMeasuredAt: string | null;
  vitalMethod?: string | null;
  vitalUnit?: string | null;
};
export type PatientVitalsResponse = {
  patientVitals: PatientVital[];
};
export type PatientNursingQuestion = {
  answers?: Array<{
    answerText: unknown;
    createdAt?: string | null;
    updatedAt?: string | null;
  }>;
  id: number;
  isVBP?: boolean;
  isVital?: boolean;
  possibleAnswers?: Array<{ text: string }>;
  questionText: string;
};
export type PatientNursingQuestionsResponse = {
  questions: PatientNursingQuestion[];
};
export type PatientUploadedDocument = {
  caregiver:
    | number
    | {
        displayName?: string | null;
        id?: number;
      };
  createdAt: string;
  docType?: { name?: string | null } | null;
  fileUrl?: string | null;
  id?: number;
  uploadId?: number;
};
export type PatientUploadedDocumentsResponse = {
  documents: PatientUploadedDocument[];
};
export type PatientVbpReport = {
  answers: Array<{
    answer?: string | null;
    notes?: string | null;
    nursingQuestionId: number;
    riskLevel?: number | null;
  }>;
  date: string;
};
export type PatientVbpReportsResponse = {
  reports: PatientVbpReport[];
};
export type PatientAdvancedDirective = {
  firstName: string | null;
  hasDNR: boolean | null;
  hasProxy: boolean | null;
  lastName: string | null;
  phoneNumber: string | null;
};
export type PatientMedicalInformationField =
  | "aideFrequency"
  | "serviceType"
  | "payerSource"
  | "medicalNotes";
export type PatientMedicalInformationProfile = Pick<
  PatientProfilePatient,
  "aideFrequency" | "allergies" | "medicalNotes" | "payerSource" | "serviceType"
>;
export type PatientMedicalInformationDraft = Record<PatientMedicalInformationField, string> & {
  allergyIds: Array<PatientAllergy["id"]>;
};
export type PatientAllergy = PatientProfilePatient["allergies"][number];
type PatchPatientBody = BodyOf<"patch", "/agencies/:agencyId/patients/:patientId">;
type PatientMedicalInformationPatch = Partial<
  Pick<PatchPatientBody, PatientMedicalInformationField>
>;

export const patientMedicalQueryKeys = {
  all: (patientId: PatientProfilePatient["id"]) => ["patientProfileModal", patientId] as const,
  availablePhysicians: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "availablePhysicians"] as const,
  bloodPressure: (
    patientId: PatientProfilePatient["id"],
    startDate: LocalDate | null,
    endDate: LocalDate | null
  ) =>
    [
      ...patientMedicalQueryKeys.all(patientId),
      "bloodPressure",
      startDate?.toString() ?? null,
      endDate?.toString() ?? null,
    ] as const,
  certificationPeriods: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "certificationPeriods"] as const,
  documents: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "documents"] as const,
  dutySheets: (patientId: PatientProfilePatient["id"], startOfWeek: LocalDate) =>
    [...patientMedicalQueryKeys.all(patientId), "dutySheets", startOfWeek.toString()] as const,
  medicalDiagnosisCodes: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "medicalDiagnosisCodes"] as const,
  medications: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "medications"] as const,
  nursingQuestions: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "nursingQuestions"] as const,
  plansOfCare: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "plansOfCare"] as const,
  primaryPhysician: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "primaryPhysician"] as const,
  patientPhysicians: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "patientPhysicians"] as const,
  medicationProfile: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "medicationProfile"] as const,
  taskHistory: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "taskHistory"] as const,
  uploadedDocuments: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "uploadedDocuments"] as const,
  vbpReports: (patientId: PatientProfilePatient["id"], startOfWeek: LocalDate) =>
    [...patientMedicalQueryKeys.all(patientId), "vbpReports", startOfWeek.toString()] as const,
  vitals: (patientId: PatientProfilePatient["id"]) =>
    [...patientMedicalQueryKeys.all(patientId), "vitals"] as const,
};

export type PatientWithAdvancedDirective = PatientProfilePatient & {
  advancedDirective?: PatientAdvancedDirective | null;
};

export function getCurrentWeekStart() {
  const today = LocalDate.now();
  return today.minusDays(today.dayOfWeek().value() - 1);
}

function createMedicalInformationDraft(
  patient: PatientMedicalInformationProfile
): PatientMedicalInformationDraft {
  return {
    aideFrequency: patient.aideFrequency ?? "",
    allergyIds: patient.allergies.map((allergy) => allergy.id),
    medicalNotes: patient.medicalNotes ?? "",
    payerSource: patient.payerSource ?? "",
    serviceType: patient.serviceType ?? "",
  };
}

function getAllergyOptions(allergies: PatientAllergy[], selectedAllergies: PatientAllergy[]) {
  const allergyById = new Map<number, PatientAllergy>();

  allergies.forEach((allergy) => allergyById.set(allergy.id, allergy));
  selectedAllergies.forEach((allergy) => allergyById.set(allergy.id, allergy));

  return Array.from(allergyById.values())
    .map((allergy) => ({ value: allergy.id, label: allergy.name }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function getPatientAllergiesLabel(allergies: PatientAllergy[]) {
  return allergies.length > 0
    ? allergies.map((allergy) => allergy.name).join(", ")
    : "No known allergies";
}

export function usePatientMedicalInformationController(
  patientId: PatientProfilePatient["id"],
  patient: PatientMedicalInformationProfile
) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const canEdit = auth.isAllowedTo("edit_patient_page_medical_info");
  const [draft, setDraft] = React.useState<PatientMedicalInformationDraft | null>(null);
  const [activeField, setActiveField] = React.useState<PatientMedicalInformationField | null>(null);
  const isEditing = draft !== null;
  const allergyTypesQuery = useQuery({
    enabled: isEditing,
    queryKey: ["patientProfileModal", "allergies"],
    queryFn: () => api.get("/agencies/:agencyId/allergies", {}),
  });
  const updatePatientMutation = useMutation({
    mutationFn: (body: PatientMedicalInformationPatch) =>
      api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId },
        body: { id: patientId, ...body },
      }),
    onSuccess: (_, body) => {
      queryClient.setQueryData(queryKeys.patient.get(patientId), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return { ...currentPatient, ...body };
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.patient.get(patientId) });
      toast({
        description: "Patient medical information updated successfully.",
        status: "success",
      });
    },
    onError: () => {
      setDraft(createMedicalInformationDraft(patient));
      toast({
        description: "Could not update patient medical information.",
        status: "error",
      });
    },
  });
  const updateAllergiesMutation = useMutation({
    mutationFn: async (params: {
      nextAllergyIds: Array<PatientAllergy["id"]>;
      previousAllergyIds: Array<PatientAllergy["id"]>;
    }) => {
      const addedAllergyIds = params.nextAllergyIds.filter(
        (allergyId) => !params.previousAllergyIds.includes(allergyId)
      );
      const removedAllergyIds = params.previousAllergyIds.filter(
        (allergyId) => !params.nextAllergyIds.includes(allergyId)
      );

      if (addedAllergyIds.length > 0) {
        await api.put(
          "/agencies/:agencyId/agency_member/:agencyMemberId/patients/:patientId/allergies",
          {
            path: { patientId },
            body: {
              allergyIds: addedAllergyIds,
              type: "ASSOCIATE",
            },
          }
        );
      }

      if (removedAllergyIds.length > 0) {
        await api.put(
          "/agencies/:agencyId/agency_member/:agencyMemberId/patients/:patientId/allergies",
          {
            path: { patientId },
            body: {
              allergyIds: removedAllergyIds,
              type: "DISSOCIATE",
            },
          }
        );
      }

      return params.nextAllergyIds;
    },
    onSuccess: (nextAllergyIds) => {
      queryClient.setQueryData(queryKeys.patient.get(patientId), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        const allergyById = new Map<number, PatientAllergy>();

        patient.allergies.forEach((allergy) => allergyById.set(allergy.id, allergy));
        (allergyTypesQuery.data?.allergies ?? []).forEach((allergy) =>
          allergyById.set(allergy.id, allergy)
        );

        return {
          ...currentPatient,
          allergies: nextAllergyIds
            .map((allergyId) => allergyById.get(allergyId))
            .filter((allergy): allergy is PatientAllergy => allergy !== undefined),
        };
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.patient.get(patientId) });
      toast({
        description: "Allergies updated successfully.",
        status: "success",
      });
    },
    onError: (_, params) => {
      setDraft((currentDraft) =>
        currentDraft === null
          ? currentDraft
          : { ...currentDraft, allergyIds: params.previousAllergyIds }
      );
      toast({
        description: "Could not update allergies.",
        status: "error",
      });
    },
  });

  const toggleEditing = () => {
    setDraft((currentDraft) => {
      if (currentDraft !== null) {
        setActiveField(null);
        return null;
      }

      return createMedicalInformationDraft(patient);
    });
  };

  const cancelField = () => {
    setDraft(createMedicalInformationDraft(patient));
    setActiveField(null);
  };

  const commitField = (field: PatientMedicalInformationField) => {
    if (draft === null) {
      return;
    }

    updatePatientMutation.mutate(
      { [field]: draft[field] },
      {
        onSuccess: () => setActiveField(null),
      }
    );
  };

  const commitAllergies = (nextAllergyIds: Array<PatientAllergy["id"]> | undefined) => {
    if (draft === null) {
      return;
    }

    const normalizedAllergyIds = nextAllergyIds ?? [];
    const previousAllergyIds = draft.allergyIds;

    setDraft({ ...draft, allergyIds: normalizedAllergyIds });
    updateAllergiesMutation.mutate({
      nextAllergyIds: normalizedAllergyIds,
      previousAllergyIds,
    });
  };

  return {
    activateField: setActiveField,
    activeField,
    allergyOptions: getAllergyOptions(allergyTypesQuery.data?.allergies ?? [], patient.allergies),
    canEdit,
    cancelField,
    commitAllergies,
    commitField,
    draft,
    isAllergyOptionsLoading: allergyTypesQuery.isLoading,
    isEditing,
    isSaving: updatePatientMutation.isPending || updateAllergiesMutation.isPending,
    setDraft,
    toggleEditing,
  };
}

export function useInvalidatePatientMedicalQueries(patientId: PatientProfilePatient["id"]) {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.patient.get(patientId) });
    void queryClient.invalidateQueries({ queryKey: patientMedicalQueryKeys.all(patientId) });
  }, [patientId, queryClient]);
}

export function usePatientPlansOfCare(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.plansOfCare(patientId),
    queryFn: () => api.get("./patients/:patientId/plans_of_care", { path: { patientId } }),
  });
}

export function usePatientCertificationPeriods(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.certificationPeriods(patientId),
    queryFn: () =>
      api.get("./patients/:patientId/certification_periods", {
        path: { patientId },
      }) as unknown as Promise<PatientCertificationPeriodsResponse>,
  });
}

export function useDeletePatientCertificationPeriod(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();

  return useMutation({
    mutationFn: (certificationPeriodId: PatientCertificationPeriod["id"]) =>
      api.delete("./patients/:patientId/certification_periods/:certificationPeriodId", {
        path: { patientId, certificationPeriodId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: patientMedicalQueryKeys.certificationPeriods(patientId),
      });
      toast({
        description: "Certification period deleted.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Can't delete certification period.",
        status: "error",
      });
    },
  });
}

export function usePatientVitals(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.vitals(patientId),
    queryFn: () =>
      api.anyRequest<PatientVitalsResponse>(
        "GET",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patient/:patientId/vital_questions",
        { path: { patientId } }
      ),
  });
}

export function usePatientNursingQuestions(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.nursingQuestions(patientId),
    queryFn: () =>
      api.anyRequest<PatientNursingQuestionsResponse>(
        "GET",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/nursing_questions",
        { path: { patientId } }
      ),
  });
}

export function usePatientUploadedDocuments(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.uploadedDocuments(patientId),
    queryFn: () =>
      api.anyRequest<PatientUploadedDocumentsResponse>(
        "GET",
        "/agencies/:agencyId/agency_member/:agencyMemberId/patient/:patientId/uploaded_documents",
        { path: { patientId } }
      ),
  });
}

export function usePatientVbpReports(
  patientId: PatientProfilePatient["id"],
  startOfWeek: LocalDate,
  endOfWeek: LocalDate
) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.vbpReports(patientId, startOfWeek),
    queryFn: () =>
      api.anyRequest<PatientVbpReportsResponse>(
        "GET",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/vbp_items",
        {
          path: { patientId },
          query: { from: startOfWeek, to: endOfWeek },
        }
      ),
    placeholderData: keepPreviousData,
  });
}

export function usePatientDutySheets(
  patientId: PatientProfilePatient["id"],
  startOfWeek: LocalDate
) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.dutySheets(patientId, startOfWeek),
    queryFn: () =>
      api.get("/agencies/:agencyId/patients/:patientId/duty_sheets", {
        path: { patientId },
        query: { startOfWeek },
      }),
  });
}

export function useAnswerPatientDutySheet(
  patientId: PatientProfilePatient["id"],
  startOfWeek: LocalDate
) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();

  return useMutation({
    mutationFn: (params: {
      completed: boolean;
      planOfCareItemId: PatientDutySheetsResponse["rows"][number]["planOfCare"]["duties"][number]["item"]["id"];
      /** Null on an aggregated plan-scheduled row, where the server means the first. */
      occurrenceIndex: number | null;
      visitInstanceId: PatientDutySheetsResponse["rows"][number]["dutySheets"][number]["visitInstanceId"];
    }) =>
      api.post("./visit_instances/:visitInstanceId/duty_sheets/answer", {
        path: { visitInstanceId: params.visitInstanceId },
        body: {
          completed: params.completed,
          dutyId: params.planOfCareItemId,
          // Omitted rather than sent as null: the field is optional and absent
          // means occurrence 1, which is what a row without one has always meant.
          ...(params.occurrenceIndex === null ? {} : { occurrenceIndex: params.occurrenceIndex }),
        },
      }),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: patientMedicalQueryKeys.dutySheets(patientId, startOfWeek),
      });
      toast({
        description: `Duty task has been marked as ${
          params.completed ? "completed" : "incomplete"
        }.`,
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update duty task.",
        status: "error",
      });
    },
  });
}

export function useSubmitPatientDutySheetNote(
  patientId: PatientProfilePatient["id"],
  startOfWeek: LocalDate
) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();

  return useMutation({
    mutationFn: (params: {
      notes: string | null;
      planOfCareItemId: PatientDutySheetsResponse["rows"][number]["planOfCare"]["duties"][number]["item"]["id"];
      /** Null on an aggregated plan-scheduled row, where the server means the first. */
      occurrenceIndex: number | null;
      visitInstanceId: PatientDutySheetsResponse["rows"][number]["dutySheets"][number]["visitInstanceId"];
    }) =>
      api.post("./visit_instances/:visitInstanceId/duty_sheets/submit_note", {
        path: { visitInstanceId: params.visitInstanceId },
        body: {
          dutyId: params.planOfCareItemId,
          notes: params.notes,
          ...(params.occurrenceIndex === null ? {} : { occurrenceIndex: params.occurrenceIndex }),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: patientMedicalQueryKeys.dutySheets(patientId, startOfWeek),
      });
      toast({
        description: "Note has been saved.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Failed to save note.",
        status: "error",
      });
    },
  });
}

export function usePatientPhysicians(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  const primaryPhysicianQuery = useQuery({
    queryKey: patientMedicalQueryKeys.primaryPhysician(patientId),
    queryFn: () => api.get("./patients/:patientId/primary_physician", { path: { patientId } }),
  });

  const availablePhysiciansQuery = useQuery({
    queryKey: patientMedicalQueryKeys.availablePhysicians(patientId),
    queryFn: () => api.get("./patients/:patientId/available_physicians", { path: { patientId } }),
  });

  return { availablePhysiciansQuery, primaryPhysicianQuery };
}

export function usePatientPhysicianAssociations(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.patientPhysicians(patientId),
    queryFn: () =>
      api.anyRequest<PatientPhysiciansResponse>(
        "GET",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/physicians",
        { path: { patientId } }
      ),
  });
}

export function usePatientMedications(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.medications(patientId),
    queryFn: () => api.get("./patients/:patientId/medications", { path: { patientId } }),
  });
}

export function usePatientMedicationProfile(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.medicationProfile(patientId),
    queryFn: () =>
      api.anyRequest<PatientMedicationProfileResponse>(
        "GET",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patient/:patientId/medication_profile",
        { path: { patientId } }
      ),
  });
}

export function usePatientDocuments(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.documents(patientId),
    queryFn: () =>
      api.get("/agencies/:agencyId/:agencyMemberId/patient_documents_history/:patientId", {
        path: { patientId },
      }),
  });
}

export function usePatientBloodPressure(
  patientId: PatientProfilePatient["id"],
  startDate: LocalDate | null,
  endDate: LocalDate | null
) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.bloodPressure(patientId, startDate, endDate),
    queryFn: () =>
      api.get("./patient/:patientId/patient_blood_pressure/history", {
        path: { patientId },
        query: {
          from: startDate ?? undefined,
          to: endDate ?? undefined,
        },
      }),
  });
}

export function useAssignPatientBloodPressureDevice(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();

  return useMutation({
    mutationFn: (imei: string) =>
      api.put("./patient/:patientId/patient_blood_pressure_device/assign/:imei", {
        path: { patientId, imei },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientMedicalQueryKeys.all(patientId) });
      toast({ description: "Blood pressure device assigned.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not assign blood pressure device.", status: "error" });
    },
  });
}

export function useUnassignPatientBloodPressureDevice(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();

  return useMutation({
    mutationFn: () =>
      api.put("./patient/:patientId/patient_blood_pressure_device/unassign", {
        path: { patientId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientMedicalQueryKeys.all(patientId) });
      toast({ description: "Blood pressure device unassigned.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not unassign blood pressure device.", status: "error" });
    },
  });
}

export function usePatientMedicalDiagnosisCodes(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.medicalDiagnosisCodes(patientId),
    queryFn: () =>
      api.get("./patients/:patientId/patient_medical_diagnosis_codes", { path: { patientId } }),
  });
}

export function usePatientTaskHistory(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: patientMedicalQueryKeys.taskHistory(patientId),
    queryFn: () => api.get("./patients/:patientId/task_history_summary", { path: { patientId } }),
  });
}

export function useUpdatePatientAdvancedDirective(patientId: PatientProfilePatient["id"]) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();

  return useMutation({
    mutationFn: (body: PatientAdvancedDirective) =>
      api.put("./patients/:patientId/advanced_directive", {
        path: { patientId },
        body,
      }),
    onSuccess: (_, body) => {
      queryClient.setQueryData(queryKeys.patient.get(patientId), (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          advancedDirective: body,
        };
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.patient.get(patientId) });
      toast({
        description: "Successfully saved advanced directives.",
        status: "success",
      });
    },
    onError: () => {
      toast({
        description: "Could not update advanced directives.",
        status: "error",
      });
    },
  });
}
